import { Link } from "react-router-dom";
import { Pencil, Phone, Mail, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AvailBadge, AssignBadge } from "@/components/atlas/Shared";
import { GhostButton } from "@/components/atlas/Fields";
import { useFetch, usePositions } from "@/lib/positions";
import { useLang } from "@/lib/i18n";
import { fmtShort } from "@/lib/format";

export const EmployeeSheet = ({ employeeId, onClose, onEdit, onDelete }) => {
  const { t, lang } = useLang();
  const { label } = usePositions();
  const { data } = useFetch(employeeId ? `/employees/${employeeId}` : null);
  const e = data?.employee?.id === employeeId ? data.employee : null;

  return (
    <Sheet open={!!employeeId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto rounded-none" data-testid="employee-sheet">
        {e && (
          <>
            <SheetHeader className="text-left">
              <div className="eyebrow">{label(e.primary_position)}</div>
              <SheetTitle className="font-display text-4xl font-semibold" data-testid="employee-sheet-name">{e.name}</SheetTitle>
            </SheetHeader>
            <div className="mt-5 space-y-2 text-sm">
              <a href={`mailto:${e.email}`} className="flex items-center gap-2 text-neutral-700"><Mail className="w-4 h-4 text-[#C5A059]" />{e.email}</a>
              {e.phone && <a href={`tel:${e.phone}`} className="flex items-center gap-2 text-neutral-700"><Phone className="w-4 h-4 text-[#C5A059]" />{e.phone}</a>}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-4">
              {e.positions.map((p) => <span key={p} className="text-[11px] border border-black/10 px-2 py-0.5">{label(p)}</span>)}
            </div>
            {e.preferred_areas?.length > 0 && <p className="text-xs text-neutral-500 mt-3">{e.preferred_areas.join(" · ")}</p>}
            {e.notes && <div className="mt-4 p-3 bg-neutral-50 border text-sm"><div className="eyebrow mb-1">{t("emp.notes")}</div>{e.notes}</div>}
            <div className="flex flex-wrap gap-3 mt-5">
              <GhostButton data-testid="employee-sheet-edit" onClick={() => onEdit(e)}><Pencil className="w-4 h-4" />{t("c.edit")}</GhostButton>
              {e.status === "inactive" && (
                <button data-testid="employee-sheet-delete" onClick={() => onDelete(e)} className="press inline-flex items-center gap-2 h-10 px-4 rounded-full border border-red-500/40 text-red-500 text-sm hover:bg-red-500/10"><Trash2 className="w-4 h-4" />{t("emp.delete")}</button>
              )}
            </div>

            <h3 className="font-display text-2xl mt-8 mb-3">{t("emp.upcoming")}</h3>
            <div className="space-y-2" data-testid="employee-assignments">
              {data.assignments.length === 0 && <p className="text-sm text-neutral-400">—</p>}
              {data.assignments.map((a) => (
                <Link key={a.id} to={`/admin/events/${a.event_id}`} onClick={onClose} className="press flex items-center justify-between gap-3 border border-black/[0.07] p-3 hover:border-[#C5A059]">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{a.event_name}</div>
                    <div className="text-xs text-neutral-500 capitalize">{fmtShort(a.event_date, lang)} · {label(a.position_key)}</div>
                  </div>
                  <AssignBadge status={a.status} />
                </Link>
              ))}
            </div>
            <h3 className="font-display text-2xl mt-8 mb-3">{t("emp.history")}</h3>
            <div className="space-y-2" data-testid="employee-availability-history">
              {data.availability.length === 0 && <p className="text-sm text-neutral-400">—</p>}
              {data.availability.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-3 border-b border-black/[0.05] py-2">
                  <div className="min-w-0">
                    <div className="text-sm truncate">{a.event_name}</div>
                    <div className="text-xs text-neutral-500 capitalize">{fmtShort(a.event_date, lang)}{a.positions.length ? ` · ${a.positions.map(label).join(", ")}` : ""}</div>
                  </div>
                  <AvailBadge status={a.status} />
                </div>
              ))}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
